import type { FrameCategory, FrameOption } from '@renderer/types/configState'
import { photoColumnsOf } from '@renderer/lib/stickers/layoutSet'

/**
 * Bingkai Kenangan sells one decision as two: "4 cut" and "8 cut" (screen 3).
 *
 * That choice is modelled as a frame CATEGORY, not a bespoke page — see the plan
 * §2.2 (D4). The category a guest picks decides the layout, and because
 * `captureMode: 'frame-based'` counts a frame's unique `photo-N` slots
 * (view/Capture.tsx:191), picking the 8-cut category makes capture count to 8 on
 * its own. The borders inside that category are what `edit` offers later.
 */

/** Unique `photo-N` slots in a frame — i.e. how many shots it asks for. */
export const slotCountOf = (frame: FrameOption | undefined): number => {
  if (!frame) return 0
  const types = new Set(
    frame.drawParams.images.map((img) => img.type).filter((type) => type.startsWith('photo-'))
  )
  return types.size
}

/**
 * How many shots a category implies, read from its frames rather than from its
 * name — a category called "8 cut" whose frames carry four slots would shoot
 * four, and the number on screen has to match what actually happens.
 */
export const slotCountOfCategory = (category: FrameCategory | undefined): number =>
  slotCountOf(category?.frames?.[0])

/** The category holding a given frame — how `edit` recovers the guest's cut. */
export const categoryOfFrame = (
  categories: FrameCategory[],
  frameId: string | undefined | null
): FrameCategory | undefined =>
  frameId
    ? categories.find((category) => (category.frames ?? []).some((f) => f._id === frameId))
    : undefined

/**
 * Whether a border belongs on the "more design" page — the bk x brenbaru
 * collaboration set (screen 7) rather than the house set (screen 6).
 *
 * Checked on tags first and the name second, so a border can be moved between
 * the two pages from the dashboard without a release.
 */
export const isCollabFrame = (frame: FrameOption): boolean =>
  frame.tags.some((tag) => tag.toLowerCase().includes('brenbaru')) ||
  frame.name.toLowerCase().includes('brenbaru')

/** One photo window, as stored in `drawParams.images`. */
export type PhotoSlot = FrameOption['drawParams']['images'][number]

/**
 * A frame's photo windows grouped into columns, left to right, each read top
 * to bottom — the shared grouping the sticker-set layout also uses.
 */
export const photoColumns = (frame: FrameOption | undefined): PhotoSlot[][] =>
  photoColumnsOf(frame?.drawParams)

/**
 * A frame's photo windows in the order photographs fill them: down the left
 * column first, then down the right.
 *
 * This decides which shot lands in which window, and it matters most on the
 * eight-cut sheet. Its two columns are cut apart into two strips, so ordering
 * row-major would deal shots 1 and 2 to opposite strips and leave each one a
 * jumble; column-major keeps each printed strip a run of consecutive shots.
 */
export const orderedSlots = (frame: FrameOption | undefined): PhotoSlot[] =>
  photoColumns(frame).flat()

/**
 * The one aspect ratio every Bingkai Kenangan photograph is captured at.
 *
 * BK's borders do not agree on a window shape — measured across the ten
 * designs there are four, from 0.739 (basic bk 6/7) to 0.881 (basic bk 5,
 * brenbaru 2) — and the guest picks the border AFTER the shutter. So one
 * capture has to serve all of them, and `object-fit: cover` decides what is
 * lost:
 *
 *   photo NARROWER than the window  ->  cropped top and bottom  (heads)
 *   photo WIDER    than the window  ->  cropped left and right  (shoulders)
 *
 * Capturing at the narrowest shape is the tempting choice and the wrong one: it
 * makes every wider border crop vertically, which takes the top off a head.
 * 0.855 is the shape six of the ten designs already use, so it is exact on
 * those, about 3% on the two widest, and leaves a side crop only on basic bk
 * 6/7 — the axis where a trim costs shoulders instead of faces.
 *
 * If the designer ever normalises the windows (the real fix), set this to
 * whatever they settle on and the whole problem goes away.
 */
export const BK_CAPTURE_RATIO = 0.855

export const photoRatioOf = (frame: FrameOption | undefined): number | null => {
  const slot = frame?.drawParams.images.find((img) => img.type.startsWith('photo-'))
  return slot ? slot.width / slot.height : null
}
